/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useCallback } from 'react';
import type { CityStats, Grid, NewsItem, ResearchState } from '../types';
import { playUiSfx } from '../utils/uiAudio';
import {
  resolveLaunchProduct,
  resolveProcessLoan,
  resolveStartResearchProject,
  resolvePitchInvestors,
} from '../src/adapters/ui/hooks/useEconomyMutations';

export interface UseEconomyHandlersDeps {
  grid: Grid;
  stats: CityStats;
  setStats: React.Dispatch<React.SetStateAction<CityStats>>;
  research: ResearchState;
  setResearch: React.Dispatch<React.SetStateAction<ResearchState>>;
  hasJumpGate: boolean;
  addNewsItem: (item: NewsItem) => void;
}

export function useEconomyHandlers({
  grid,
  stats,
  setStats,
  research,
  setResearch,
  hasJumpGate,
  addNewsItem,
}: UseEconomyHandlersDeps) {
  const handleLaunchProduct = useCallback(
    (productId: string) => {
      const result = resolveLaunchProduct({ productId, grid, stats });

      if (result.skipped) {
        return;
      }

      if (result.newsMessage) {
        addNewsItem({
          id: Date.now().toString(),
          text: result.newsMessage,
          type: result.newsType ?? 'neutral',
        });
      }

      if (result.ok) {
        setStats(result.stats);
      }

      if (result.sfx) {
        playUiSfx(result.sfx);
      }
    },
    [addNewsItem, grid, setStats, stats],
  );

  const handleStartResearch = useCallback(
    (techId: string) => {
      const result = resolveStartResearchProject({ techId, stats, research });

      if (result.skipped) {
        return;
      }

      if (result.newsMessage) {
        addNewsItem({
          id: Date.now().toString(),
          text: result.newsMessage,
          type: result.newsType ?? 'neutral',
        });
      }

      if (result.ok) {
        setStats(result.stats);
        setResearch(result.research);
      }

      if (result.sfx) {
        playUiSfx(result.sfx);
      }
    },
    [addNewsItem, research, setResearch, setStats, stats],
  );

  const handleTakeLoan = useCallback(() => {
    const result = resolveProcessLoan({ action: 'take', stats });

    if (result.newsMessage) {
      addNewsItem({
        id: Date.now().toString(),
        text: result.newsMessage,
        type: result.newsType ?? 'neutral',
      });
    }

    if (result.ok) {
      setStats(result.stats);
    }

    if (result.sfx) {
      playUiSfx(result.sfx);
    }
  }, [addNewsItem, setStats, stats]);

  const handleRepayLoan = useCallback(() => {
    const result = resolveProcessLoan({ action: 'repay', stats });

    if (result.newsMessage) {
      addNewsItem({
        id: Date.now().toString(),
        text: result.newsMessage,
        type: result.newsType ?? 'neutral',
      });
    }

    if (result.ok) {
      setStats(result.stats);
    }

    if (result.sfx) {
      playUiSfx(result.sfx);
    }
  }, [addNewsItem, setStats, stats]);

  const handlePitchInvestors = useCallback(() => {
    const result = resolvePitchInvestors({ stats, hasJumpGate });

    if (result.newsMessage) {
      addNewsItem({
        id: Date.now().toString(),
        text: result.newsMessage,
        type: result.newsType ?? 'neutral',
      });
    }

    if (result.stats !== stats) {
      setStats(result.stats);
    }

    if (result.playErrorSfx) {
      playUiSfx('error');
    } else if (result.sfx) {
      playUiSfx(result.sfx);
    }
  }, [addNewsItem, hasJumpGate, setStats, stats]);

  return {
    handleLaunchProduct,
    handleStartResearch,
    handleTakeLoan,
    handleRepayLoan,
    handlePitchInvestors,
  };
}